import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ChatBox from "./ChatBox";
import UserLink from "./UserLink";
import { fetchWithAuth } from "../utils/api";
import "./DiveManagerModal.css";

const DiveManagerModal = ({ dive, isOrganizer, currentUser, onClose, onUpdated }) => {
    const { t } = useTranslation();
    const apiUrl = process.env.REACT_APP_API_URL;
    const [activeTab, setActiveTab] = useState("participants");
    const [participants, setParticipants] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [formData, setFormData] = useState({});
    const [siteName, setSiteName] = useState("");
    const [allSites, setAllSites] = useState([]);
    const [changingSite, setChangingSite] = useState(false);
    const [siteSearch, setSiteSearch] = useState("");
    const [userSearch, setUserSearch] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false); // modale de confirmation annulation/désinscription
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [organizerUser, setOrganizerUser] = useState(null);

    // Charger les participants
    useEffect(() => {
        if (dive?.id) {
            fetchWithAuth(`${apiUrl}/dive-participants/${dive.id}`)
                .then((r) => r.json())
                .then((data) => Array.isArray(data) ? setParticipants(data) : setParticipants([]))
                .catch((err) => console.error("Erreur participants :", err));
        }
    }, [dive, apiUrl]);

    useEffect(() => {
        if (!dive?.user_id) return;

        fetchWithAuth(`${apiUrl}/users/${dive.user_id}`)
            .then((r) => r.json())
            .then((data) => setOrganizerUser(data || null))
            .catch((err) => console.error("Erreur chargement organisateur :", err));
    }, [dive?.user_id, apiUrl]);

    // Charger les utilisateurs déjà invités
    useEffect(() => {
        if (dive?.id) {
            fetchWithAuth(`${apiUrl}/dive-intentions/${dive.id}`)
                .then((r) => r.json())
                .then((data) => {
                    if (data && Array.isArray(data.invited_users)) {
                        setFormData((prev) => ({
                            ...prev,
                            invited_users: data.invited_users,
                        }));
                    }
                })
                .catch((err) => console.error("Erreur chargement invited_users :", err));
        }
    }, [dive, apiUrl]);


    // Charger les utilisateurs
    useEffect(() => {
        fetchWithAuth(`${apiUrl}/users`)
            .then((r) => r.json())
            .then((data) => setAllUsers(Array.isArray(data) ? data : []))
            .catch((err) => console.error("Erreur utilisateurs :", err));
    }, [apiUrl]);

    useEffect(() => {
        const lower = userSearch.toLowerCase();
        setFilteredUsers(
            allUsers.filter((u) =>
                `${u.first_name} ${u.last_name}`.toLowerCase().includes(lower)
            )
        );
    }, [userSearch, allUsers]);

    // Préremplir le formulaire
    useEffect(() => {
        if (dive) {
            setFormData({
                site_id: dive.site_id || dive.site?.id || "",
                planned_date: dive.planned_date || "",
                planned_time: dive.planned_time || "",
                notes: dive.notes || "",
                dive_type: dive.dive_type || "",
                gas: dive.gas || "",
                config: dive.config || "",
                visibility: dive.visibility || "public",
                invited_users: dive.invited_users || [],
            });
            setSiteName(dive.site?.name || "");
        }
    }, [dive]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((s) => ({ ...s, [name]: value }));
    };

    // Sauvegarde de la modification
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await fetchWithAuth(`${apiUrl}/dive-intentions/${dive.id}`, {
                method: "PUT",
                body: JSON.stringify(formData),
            });
            if (!res.ok) throw new Error(t('diveManager.alerts.updateError'));
            toast.success(t('diveManager.alerts.diveUpdated'));
            await onUpdated?.();
            onClose();
        } catch (err) {
            console.error(err);
            toast.error(err.message);
        }
    };

    // Annulation / désinscription (après confirmation via la modale stylée)
    const doCancelOrUnsubscribe = async () => {
        setConfirmOpen(false);
        try {
            const endpoint = isOrganizer
                ? `${apiUrl}/dive-intentions/${dive.id}`
                : `${apiUrl}/unsubscribe-dive`;

            const options = isOrganizer
                ? { method: "DELETE" }
                : {
                    method: "POST",
                    body: JSON.stringify({ dive_intention_id: dive.id }),
                };

            const res = await fetchWithAuth(endpoint, options);
            if (!res.ok) throw new Error(t('diveManager.alerts.actionError'));
            toast.success(isOrganizer ? t('diveManager.alerts.diveCancelled') : t('diveManager.alerts.unsubscribed'));
            await onUpdated?.();
            onClose();
        } catch (err) {
            console.error(err);
            toast.error(err.message);
        }
    };

    return (
        <div className="manager-overlay" onClick={onClose}>
            <div className="manager-modal" onClick={(e) => e.stopPropagation()}>
                <h2 style={{ color: "var(--primary-blue)" }}>
                    {t('diveManager.title')} – {dive.site?.name || t('diveManager.unknownSite')}
                </h2>

                {/* Onglets */}
                <div className="tabs">
                    <button
                        className={activeTab === "participants" ? "active" : ""}
                        onClick={() => setActiveTab("participants")}
                    >
                        {t('diveManager.tabs.participants')}
                    </button>
                    <button
                        className={activeTab === "chat" ? "active" : ""}
                        onClick={() => setActiveTab("chat")}
                    >
                        {t('diveManager.tabs.chat')}
                    </button>
                    {isOrganizer && (
                        <>
                            <button
                                className={activeTab === "modifier" ? "active" : ""}
                                onClick={() => setActiveTab("modifier")}
                            >
                                {t('diveManager.tabs.modify')}
                            </button>
                            <button
                                className={activeTab === "inviter" ? "active" : ""}
                                onClick={() => setActiveTab("inviter")}
                            >
                                {t('diveManager.tabs.invite')}
                            </button>
                        </>
                    )}
                </div>

                {/* Contenu dynamique */}
                <div className="tab-content">
                    {activeTab === "participants" && (
                        <div>
                            {(() => {
                                const organizer = organizerUser
                                    ? { user_id: organizerUser.id, users: organizerUser }
                                    : null;

                                const apiParticipants = Array.isArray(participants) ? participants : [];
                                const merged = organizer ? [organizer, ...apiParticipants] : apiParticipants;

                                const seen = new Set();
                                const list = merged.filter((p) => {
                                    if (!p?.user_id) return false;
                                    if (seen.has(p.user_id)) return false;
                                    seen.add(p.user_id);
                                    return true;
                                });

                                if (list.length === 0) {
                                    return <p>{t('diveManager.participants.noParticipants')}</p>;
                                }

                                return (
                                    <ul>
                                        {list.map((p) => (
                                            <li key={p.user_id}>
                                                <UserLink id={p.user_id} firstName={p.users?.first_name} lastName={p.users?.last_name} />
                                                {p.user_id === dive.user_id ? ` ${t('diveManager.participants.organizer')}` : ""}
                                            </li>
                                        ))}
                                    </ul>
                                );
                            })()}
                        </div>
                    )}

                    {activeTab === "chat" && (
                        <ChatBox diveIntentionId={dive.id} currentUser={currentUser} organizer={organizerUser} />
                    )}

                    {activeTab === "modifier" && isOrganizer && (
                        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            {/* Site */}
                            <div style={{ display: "flex", alignItems: changingSite ? "flex-start" : "center", gap: "10px" }}>
                                <label style={{ width: "130px", paddingTop: changingSite ? 8 : 0 }}>{t('diveForm.labels.site')}</label>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    {!changingSite ? (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setChangingSite(true); setSiteSearch("");
                                                if (allSites.length === 0) {
                                                    fetchWithAuth(`${apiUrl}/diving-sites`).then((r) => (r.ok ? r.json() : [])).then((d) => setAllSites(Array.isArray(d) ? d : [])).catch(() => {});
                                                }
                                            }}
                                            style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, borderRadius: 6, border: "1px solid #ccc", padding: 6, background: "#fff", cursor: "pointer", fontSize: 14, color: "#223344" }}
                                        >
                                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{siteName || "—"}</span>
                                            <span style={{ color: "#8aa0a8", fontSize: 12 }}>▾</span>
                                        </button>
                                    ) : (
                                        <div>
                                            <input autoFocus type="text" placeholder={t('create.searchPlaceholder')} value={siteSearch} onChange={(e) => setSiteSearch(e.target.value)} style={{ width: "100%", padding: 6, fontSize: 14, border: "1px solid var(--primary-blue)", borderRadius: 6, boxSizing: "border-box" }} />
                                            <ul style={{ listStyle: "none", margin: "6px 0 0", padding: 0, maxHeight: 160, overflowY: "auto", border: "1px solid #eef2f4", borderRadius: 8 }}>
                                                {(() => {
                                                    const term = siteSearch.trim().toLowerCase();
                                                    const list = (Array.isArray(allSites) ? allSites : []).filter((s) => s?.name && (!term || s.name.toLowerCase().includes(term))).sort((a, b) => a.name.localeCompare(b.name)).slice(0, 40);
                                                    if (list.length === 0) return <li style={{ padding: 10, color: "#777", textAlign: "center", fontSize: 13 }}>{t('create.noResults')}</li>;
                                                    return list.map((s) => (
                                                        <li key={s.id}>
                                                            <button type="button" onClick={() => { setFormData((f) => ({ ...f, site_id: s.id })); setSiteName(s.name); setChangingSite(false); setSiteSearch(""); }} style={{ width: "100%", textAlign: "left", background: s.id === formData.site_id ? "rgba(0,150,199,0.10)" : "#fff", border: "none", borderBottom: "1px solid #f0f0f0", padding: "8px", cursor: "pointer", fontSize: 14, color: "#223344", display: "flex", justifyContent: "space-between", gap: 8 }}>
                                                                <span>{s.name}{s.id === formData.site_id ? " ✓" : ""}</span>
                                                                {s.canton && <span style={{ color: "#8aa0a8", fontSize: 12 }}>{s.canton}</span>}
                                                            </button>
                                                        </li>
                                                    ));
                                                })()}
                                            </ul>
                                            <button type="button" onClick={() => { setChangingSite(false); setSiteSearch(""); }} style={{ background: "none", border: "none", color: "#999", cursor: "pointer", fontSize: 12, marginTop: 4, padding: 0 }}>{t('common.cancel')}</button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Date */}
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <label style={{ width: "130px" }}>{t('diveForm.labels.date')}</label>
                                <input
                                    type="date"
                                    name="planned_date"
                                    value={formData.planned_date}
                                    onChange={handleInputChange}
                                    style={{ flex: 1, borderRadius: "6px", border: "1px solid #ccc", padding: "6px" }}
                                />
                            </div>

                            {/* Heure */}
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <label style={{ width: "130px" }}>{t('diveForm.labels.time')}</label>
                                <input
                                    type="time"
                                    name="planned_time"
                                    value={formData.planned_time}
                                    onChange={handleInputChange}
                                    style={{ flex: 1, borderRadius: "6px", border: "1px solid #ccc", padding: "6px" }}
                                />
                            </div>

                            {/* Type de plongée */}
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <label style={{ width: "130px" }}>{t('diveForm.labels.diveType')}</label>
                                <select
                                    name="dive_type"
                                    value={formData.dive_type}
                                    onChange={handleInputChange}
                                    style={{
                                        flex: 1,
                                        borderRadius: "6px",
                                        border: "1px solid #ccc",
                                        padding: "6px",
                                    }}
                                >
                                    <option value="">{t('common.select')}</option>
                                    <option value="egal">{t('filters.anyChoice')}</option>
                                    <option value="loisir">{t('diveTypes.loisir')}</option>
                                    <option value="deco">{t('diveTypes.deco')}</option>
                                </select>
                            </div>

                            {/* Configuration */}
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <label style={{ width: "130px" }}>{t('diveForm.labels.config')}</label>
                                <select
                                    name="config"
                                    value={formData.config}
                                    onChange={handleInputChange}
                                    style={{ flex: 1, borderRadius: "6px", border: "1px solid #ccc", padding: "6px" }}
                                >
                                    <option value="">{t('common.select')}</option>
                                    <option value="egal">{t('filters.anyChoice')}</option>
                                    <option value="oc">{t('configTypes.oc')}</option>
                                    <option value="cc">{t('configTypes.cc')}</option>
                                </select>
                            </div>

                            {/* Gaz */}
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <label style={{ width: "130px" }}>{t('diveForm.labels.gas')}</label>
                                <select
                                    name="gas"
                                    value={formData.gas}
                                    onChange={handleInputChange}
                                    style={{ flex: 1, borderRadius: "6px", border: "1px solid #ccc", padding: "6px" }}
                                >
                                    <option value="">{t('common.select')}</option>
                                    <option value="egal">{t('filters.anyChoice')}</option>
                                    <option value="air">{t('gasTypes.air')}</option>
                                    <option value="nx">{t('gasTypes.nx')}</option>
                                    <option value="tx">{t('gasTypes.tx')}</option>
                                </select>
                            </div>

                            {/* Notes */}
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                                <label style={{ width: "130px", paddingTop: "6px" }}>{t('diveForm.labels.notes')}</label>
                                <textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    rows="2"
                                    style={{
                                        flex: 1,
                                        borderRadius: "6px",
                                        border: "1px solid #ccc",
                                        padding: "6px",
                                        fontSize: "14px",
                                    }}
                                />
                            </div>

                            {/* Visibilité */}
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <label style={{ width: "130px" }}>{t('diveForm.labels.visibility')}</label>
                                <select
                                    name="visibility"
                                    value={formData.visibility}
                                    onChange={handleInputChange}
                                    style={{ flex: 1, borderRadius: "6px", border: "1px solid #ccc", padding: "6px" }}
                                >
                                    <option value="public">{t('diveForm.visibility.public')}</option>
                                    <option value="friends">{t('diveForm.visibility.friends')}</option>
                                    <option value="private">{t('diveForm.visibility.private')}</option>
                                </select>
                            </div>

                            {/* Bouton */}
                            <div style={{ textAlign: "center", marginTop: "10px" }}>
                                <button
                                    type="submit"
                                    className="primary-btn"
                                    style={{
                                        width: "100%",
                                        padding: "10px",
                                        borderRadius: "8px",
                                        fontWeight: "bold",
                                        fontSize: "15px",
                                    }}
                                >
                                    {t('common.save')}
                                </button>
                            </div>
                        </form>
                    )}


                    {activeTab === "inviter" && isOrganizer && (
                        <div>
                            <input
                                type="text"
                                placeholder={t('diveManager.invite.searchPlaceholder')}
                                value={userSearch}
                                onChange={(e) => setUserSearch(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "6px 8px",
                                    marginBottom: "10px",
                                    borderRadius: "6px",
                                    border: "1px solid #ccc",
                                }}
                            />
                            <ul style={{ listStyle: "none", padding: 0, maxHeight: 200, overflowY: "auto" }}>
                                {filteredUsers.map((u) => {
                                    const alreadyInvited = (formData.invited_users || []).includes(u.id);
                                    const alreadyParticipant = participants.some((p) => p.user_id === u.id);
                                    const isOrganizer = u.id === dive.user_id;
                                    const isDisabled = alreadyInvited || alreadyParticipant || isOrganizer;

                                    let buttonText = t('diveManager.invite.invite');
                                    if (isOrganizer) {
                                        buttonText = t('diveManager.invite.organizer');
                                    } else if (alreadyParticipant) {
                                        buttonText = t('diveManager.invite.alreadyJoined');
                                    } else if (alreadyInvited) {
                                        buttonText = t('diveManager.invite.alreadyInvited');
                                    }

                                    const handleInvite = async () => {
                                        if (isDisabled) return;
                                        try {
                                            const res = await fetchWithAuth(`${apiUrl}/invite-to-dive`, {
                                                method: "POST",
                                                body: JSON.stringify({
                                                    dive_id: dive.id,
                                                    invited_user_id: u.id,
                                                }),
                                            });
                                            if (!res.ok) {
                                                const err = await res.json();
                                                if (err.error === "already_invited") {
                                                    toast.warning(t('diveManager.invite.alreadyInvitedError'));
                                                } else if (err.error === "already_participant") {
                                                    toast.info(t('diveManager.invite.alreadyJoinedError'));
                                                } else {
                                                    throw new Error(err.error);
                                                }
                                                return;
                                            }
                                            // Mise à jour locale après succès
                                            setFormData((prev) => ({
                                                ...prev,
                                                invited_users: [...(prev.invited_users || []), u.id],
                                            }));
                                        } catch (err) {
                                            console.error(err);
                                            toast.error(t('diveManager.invite.error'));
                                        }
                                    };

                                    return (
                                        <li
                                            key={u.id}
                                            style={{
                                                padding: "6px 4px",
                                                borderBottom: "1px solid #eee",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}
                                        >
                                            <span>
                                                {u.first_name} {u.last_name}
                                            </span>
                                            <button
                                                disabled={isDisabled}
                                                onClick={handleInvite}
                                                className="primary-btn"
                                                style={{
                                                    opacity: isDisabled ? 0.6 : 1,
                                                    cursor: isDisabled ? "not-allowed" : "pointer",
                                                    background: isDisabled ? "#ccc" : "var(--primary-blue)",
                                                }}
                                            >
                                                {buttonText}
                                            </button>
                                        </li>
                                    );
                                })}

                            </ul>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="modal-footer" style={{ justifyContent: "flex-end" }}>
                    {isOrganizer ? (
                        <button className="danger-btn" onClick={() => setConfirmOpen(true)}>
                            {t('diveManager.buttons.deleteDive')}
                        </button>
                    ) : (
                        <button className="danger-btn" onClick={() => setConfirmOpen(true)}>
                            {t('diveManager.buttons.unsubscribe')}
                        </button>
                    )}
                    <button className="close-btn" onClick={onClose}>
                        {t('common.close')}
                    </button>
                </div>

                {/* Confirmation stylée (remplace window.confirm) */}
                {confirmOpen && (
                    <div className="dm-confirm-overlay" onClick={() => setConfirmOpen(false)}>
                        <div className="dm-confirm" onClick={(e) => e.stopPropagation()}>
                            <p className="dm-confirm-text">
                                {isOrganizer
                                    ? t('diveManager.confirmations.cancelDive')
                                    : t('diveManager.confirmations.unsubscribe')}
                            </p>
                            <div className="dm-confirm-actions">
                                <button className="close-btn" onClick={() => setConfirmOpen(false)}>
                                    {t('common.cancel')}
                                </button>
                                <button className="danger-btn" onClick={doCancelOrUnsubscribe}>
                                    {isOrganizer
                                        ? t('diveManager.buttons.deleteDive')
                                        : t('diveManager.buttons.unsubscribe')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DiveManagerModal;
